import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import { useApiWrapperWithErrorValidation } from 'shared/hooks/use-api-wrapper-with-error-validation'
import { RequestMethodsEnum } from 'shared/hooks/use-api-wrapper-with-error-validation/types'
import type { PaginationInterface } from 'shared/types/swr-pagination-interface'
import type { UseQueryClearOptions } from 'shared/types/tanstack-utility-types'
import { templatesKeys } from '../api/key-factory'
import { TEMPLATES_API } from '../api/templates-api'
import type {
  TemplatesFilterInterface,
  TemplatesLimitType,
} from '../types/templates-filter-interface'
import type { TemplatesApiInterface } from '../types/templates-interface'

interface UseTemplatesInterface extends UseQueryClearOptions<
  TemplatesApiInterface,
  Error,
  TemplatesApiInterface
> {
  filter: TemplatesFilterInterface
  pagination: PaginationInterface
  limit: TemplatesLimitType
}

export const useTemplates = ({
  limit,
  pagination,
  filter,
  ...options
}: UseTemplatesInterface): UseQueryResult<TemplatesApiInterface> => {
  const { locale, isReady } = useRouter()
  const { fetcher } = useApiWrapperWithErrorValidation<
    RequestMethodsEnum.get,
    TemplatesApiInterface
  >({ method: RequestMethodsEnum.get })

  const filterApi = `${
    filter.category === null ? '' : `&filter[category]=${filter.category}`
  }&filter[locale]=${locale}`

  const url =
    pagination.startFromId === undefined
      ? `${TEMPLATES_API}/list?pagination[limit]=${limit}${filterApi}`
      : `${TEMPLATES_API}/list?pagination[startFrom]=${pagination.startFromId}&pagination[order]=${pagination.order}&pagination[limit]=${limit}${filterApi}`

  return useQuery({
    queryKey: templatesKeys.list(url),
    queryFn: async () => fetcher(url),
    enabled: isReady && !!locale && !!limit,
    gcTime: Infinity,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: 'always',
    ...options,
  })
}
